import { isBossRelic, RELICS } from './relics';
import { ACTIVE_RELIC_SHOP_PRICES } from './relicBalance';
import { drawRelicFromPool, isShopRelic } from './relicDrops';
import { CORE_SECTOR } from './runConstants';

// Apply the same 25% discount to rarity prices and individual upgrades.
const discountPrice = (price) => Math.max(1, Math.floor(price * 0.75));
const RUN_RARITY_PRICES = {
  Common: 2,
  Uncommon: 3,
  Rare: 6,
  Epic: 9,
  Legendary: 12,
};

export const RELIC_PRICES = Object.fromEntries(
  Object.entries(RELICS).map(([id, definition]) => [
    id,
    Math.max(
      RUN_RARITY_PRICES[definition.rarity],
      discountPrice(definition.shopPrice || ACTIVE_RELIC_SHOP_PRICES[id] || 0)
    ),
  ])
);

export const getRelicPrice = (game, id) =>
  game.portalRun &&
  game.phase === 'choose' &&
  game.floor === 2 &&
  game.relicOffers?.includes(id) &&
  RELICS[id]?.category === 'Common'
    ? 0
    : RELIC_PRICES[id];

export const getShopTier = (id, game = {}) =>
  getRelicPrice(game, id) <= 5
    ? 'Budget'
    : getRelicPrice(game, id) <= 9
    ? 'Standard'
    : 'Premium';

export const isBossShopUpgrade = (id, game) =>
  isShopRelic(id) && isBossRelic(id, game);

export const FINAL_SHOP_RARITIES = ['Uncommon', 'Rare', 'Epic', 'Legendary'];

export const getShopPurchaseId = (game) => {
  if (game.marketPurchased !== game.floor) return null;
  const saved = game.marketPurchase;
  if (
    saved?.floor === game.floor &&
    RELICS[saved.id] &&
    game.relics.includes(saved.id)
  )
    return saved.id;
  // Older saves retain the shop stock. Recover its single purchased item
  // only when that identity is unambiguous.
  const owned =
    (game.phase === 'shop' ? game.shopOffers : game.relicOffers) || [];
  const matches = owned.filter((id) => RELICS[id] && game.relics.includes(id));
  return matches.length === 1 ? matches[0] : null;
};

export const prepareShop = (game, random = Math.random) => {
  if (game.phase !== 'shop') return game;
  const isRelevantOffer = (id) =>
    isShopRelic(id) &&
    (game.floor === CORE_SECTOR
      ? isBossShopUpgrade(id, game)
      : !RELICS[id]?.bossOnly);
  // Preserve receipts and spent stock from older saves. An unspent final shop
  // upgrades once to one unowned relic from each of its four rarity bands.
  if (game.marketPurchased === game.floor) return game;
  if (game.floor === CORE_SECTOR) {
    const available = Object.keys(RELIC_PRICES).filter(
      (id) => !game.relics.includes(id) && isRelevantOffer(id)
    );
    const bands = FINAL_SHOP_RARITIES.map((rarity) =>
      available.filter((id) => RELICS[id].category === rarity)
    ).filter((pool) => pool.length);
    if (
      Array.isArray(game.shopOffers) &&
      game.shopOffers.length === bands.length &&
      game.shopOffers.every((id, index) => bands[index].includes(id))
    )
      return game;
    return {
      ...game,
      shopOffers: bands.map((pool) =>
        drawRelicFromPool(pool, undefined, random)
      ),
    };
  }
  if (
    Array.isArray(game.shopOffers) &&
    game.shopOffers.length <= 3 &&
    new Set(game.shopOffers).size === game.shopOffers.length &&
    game.shopOffers.every(
      (id) =>
        Object.prototype.hasOwnProperty.call(RELIC_PRICES, id) &&
        isRelevantOffer(id)
    )
  )
    return game;

  let available = Object.keys(RELIC_PRICES).filter(
    (id) => !game.relics.includes(id) && isRelevantOffer(id)
  );
  const shopOffers = [];
  ['Budget', 'Standard', 'Premium'].forEach((tier) => {
    if (!available.length) return;
    const inTier = available.filter((id) => getShopTier(id, game) === tier);
    const pool = game.portalRun
      ? available
      : inTier.length
      ? inTier
      : available;
    const selected = drawRelicFromPool(pool, undefined, random);
    if (!selected) return;
    shopOffers.push(selected);
    available = available.filter((id) => id !== selected);
  });
  return { ...game, shopOffers };
};
